import { useEffect, useRef } from 'react';
import { useT } from '../i18n/index.js';
import { Ico } from './Iconos.js';

/**
 * The foot of a list drawn a part at a time: «Ver más», which presses itself before it is reached
 * (the owner, 2026-10-12: «as I am scrolling down … it abruptly stops»). The next part is drawn
 * while the last rows are still a screen below, so scrolling down never meets an end that is not
 * the end. A tap does it too: a phone without `IntersectionObserver`, or a thumb faster than the list.
 *
 * Only for a list that ends its page or its sheet: one with more under it would push what follows
 * away from a thumb scrolling down to it. That one gets a «Ver más» to tap.
 */
export function VerMasAlBajar({ quedan, onMas }: { quedan: number; onMas: () => void }) {
  const t = useT();
  const boton = useRef<HTMLButtonElement>(null);
  const mas = useRef(onMas);
  mas.current = onMas;

  // Watched again after each part (`quedan` changes): a part too short to push the button out of
  // reach, on a tall screen, draws the next at once instead of waiting for a scroll.
  useEffect(() => {
    const el = boton.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const vigia = new IntersectionObserver(
      (vistas) => {
        if (vistas.some((v) => v.isIntersecting)) mas.current();
      },
      // A screen ahead of whatever scrolls: in a sheet its body, which the page's screen knows nothing of.
      { root: loQueSeDesliza(el), rootMargin: '0px 0px 100% 0px' },
    );
    vigia.observe(el);
    return () => vigia.disconnect();
  }, [quedan]);

  return (
    <button ref={boton} type="button" className="btn-secundario mt-3 w-full text-sm" onClick={onMas} data-ver-mas>
      <Ico icono="desplegar" />
      {t('comun.verMas')}
    </button>
  );
}

/**
 * The box the list scrolls in: a sheet's body (`Modal`), or null for the page. Only one that
 * scrolls up and down now: a table's `overflow-x-auto` computes as `auto` both ways, and a screen
 * ahead of a box that holds the button whole would draw every part at once.
 */
function loQueSeDesliza(el: HTMLElement): HTMLElement | null {
  for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
    const { overflowY } = getComputedStyle(p);
    if ((overflowY === 'auto' || overflowY === 'scroll') && p.scrollHeight > p.clientHeight) return p;
  }
  return null;
}
